import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { SelectWithDescriptionComponent, SelectWithDescriptionOption } from '@ss/shared/select-with-description';
import { firebaseConfig } from '@ss/typings/config';
import { Facility } from '@ss/typings/facility';
import { PccFacility, PccPatient } from '@ss/typings/pcc';
import { derivedAsync } from 'ngxtension/derived-async';
import { tap } from 'rxjs/operators';

@Component({
	selector: 'nta-link-pcc-facility',
	imports: [
		CommonModule,
		MatFormFieldModule,
		MatSelectModule,
		MatDialogModule,
		FormsModule,
		MatIconModule,
		MatInputModule,
		MatButtonModule,
		MatStepperModule,
		SelectWithDescriptionComponent,
		MatPaginatorModule,
		MatProgressSpinnerModule
	],
	templateUrl: './link-pcc-facility.dialog.html',
	styleUrls: ['./link-pcc-facility.dialog.scss'],
})
export class LinkPccFacilityDialog implements OnInit {

	// eslint-disable-next-line @typescript-eslint/no-magic-numbers
	PAGE_SIZE = 10;

	http = inject(HttpClient);

	data = inject(MAT_DIALOG_DATA);

	state = signal<'connected' | 'instructions' | 'connect' | 'match-error'>(this.data.state ?? 'connect');

	dialogRef: MatDialogRef<LinkPccFacilityDialog> = inject(MatDialogRef);

	facility = signal<Facility>(this.data.facility);

	unlinkedPccFacilities = signal<PccFacility[]>(this.data.unlinkedPccFacilities);

	unlinkedPccFacilitiesOptionsConfig = computed<SelectWithDescriptionOption[]>(() => this.unlinkedPccFacilities().map((f) => ({
		displayName: f.facilityName,
		description: `${f.addressLine1}\n${f.city}, ${f.state} ${f.postalCode}`
	})));

	selectedFacilityOption = signal<SelectWithDescriptionOption | null>(null);

	preconfiguredPccFacility = signal<PccFacility | null>(null);

	matchedByPendingIds = false;

	fullSelectedPccFacility = computed<PccFacility | null>(() => {
		const preconfigured = this.preconfiguredPccFacility();
		if (preconfigured) return preconfigured;
		const selectedOption = this.selectedFacilityOption();
		if (!selectedOption) return null;
		return this.unlinkedPccFacilities().find((f) => f.facilityName === selectedOption.displayName) ?? null;
	});

	hasUnlinkedPccFacilities = computed(() => this.unlinkedPccFacilities().length > 0);

	loadingPatients = signal(false);

	patients = derivedAsync<PccPatient[]>(() => {
		if (!this.fullSelectedPccFacility()) {
			return [];
		}
		this.loadingPatients.set(true);

		return this.http.get<PccPatient[]>(`${firebaseConfig.functionsUrl}/ntaApi/pcc/facilities/${this.fullSelectedPccFacility().facId}/patients`).pipe(tap((patients) => {
			this.loadingPatients.set(false);
		}));
	}, { initialValue: [] });

	pagedPatients = computed(() => this.patients().slice(this.page() * this.PAGE_SIZE, (this.page() + 1) * this.PAGE_SIZE));

	foundFacilityByName = false;

	page = signal(0);

	linkingPatients = signal(true);

	error = signal<string | null>(null);

	results = signal<{
		foundByBirthdayAndName: number;
		foundByPccPatientId: number;
		notFound: number;
	}>({
		foundByBirthdayAndName: 0,
		foundByPccPatientId: 0,
		notFound: 0
	});

	finalStep = computed(() => {
		if (this.linkingPatients()) return 'Linking Patients';
		if (this.error()) return 'Error Linking Patients';
		return 'Linking Complete';
	});

	canGoBack = signal(true);

	confirmText = '';

	ngOnInit() {
		const { pendingPccFacilityId, pendingPccOrgUuid } = this.facility();
		let matchingPccFacility = pendingPccFacilityId && pendingPccOrgUuid ? this.unlinkedPccFacilities().find((f) => f.facId === pendingPccFacilityId && f.orgUuid === pendingPccOrgUuid) : undefined;

		if (pendingPccFacilityId && pendingPccOrgUuid) {
			this.matchedByPendingIds = !!matchingPccFacility;
			if (matchingPccFacility) {
				this.preconfiguredPccFacility.set(matchingPccFacility);
			}
			if (!matchingPccFacility && this.data.autoLink) {
				this.state.set('match-error');
				return;
			}
		}

		if (!matchingPccFacility) {
			matchingPccFacility = this.unlinkedPccFacilities().find((f) => f.facilityName === this.facility().name);
		}

		if (matchingPccFacility && !this.matchedByPendingIds) {
			const matchingOption = this.unlinkedPccFacilitiesOptionsConfig().find((o) => o.displayName === matchingPccFacility.facilityName);
			this.selectedFacilityOption.set(matchingOption);
		}
		this.foundFacilityByName = !!matchingPccFacility && !this.matchedByPendingIds;
	}

	linkPatients() {
		if (!this.fullSelectedPccFacility()) return;
		this.canGoBack.set(false);
		this.linkingPatients.set(true);
		this.error.set(null);
		this.http.post<{
			foundByBirthdayAndName: number;
			foundByPccPatientId: number;
			notFound: number;
		}>(`${firebaseConfig.functionsUrl}/ntaApi/pcc/facility/initialize/${this.facility().facilityId}`, { pccFacility: this.fullSelectedPccFacility(), pccPatients: this.patients() }).subscribe({
			next: (results) => {
				this.results.set(results);
				this.linkingPatients.set(false);
			},
			error: (error) => {
				this.error.set(error.error.message);
				this.linkingPatients.set(false);
			}
		});
	}

	submitLink(confirmTextInput: NgModel, stepper: MatStepper) {
		confirmTextInput.control.markAsTouched();
		if (confirmTextInput.invalid) return;
		this.linkPatients();
		stepper.next();
	}
}

